import {
	IconButton,
	ListItemAvatar,
	ListItemText
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {gql, useMutation, useQuery} from "@apollo/client";
import CardElement from "components/CardElement";
import {useKeycloak} from "../../src/useKeycloak";
import {useRouter} from "next/router";
import StaticAvatar from "../StaticAvatar";
import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
	faBuilding, faMessage,
	faPhone,
	faUser,
	faUserPlus,
	faComment
} from "@fortawesome/pro-solid-svg-icons";
import Link from "next/link";
import {ListItemButtonWrapper, ListItemWrapper, ListWrapper} from "../../styles/ListComponents";
import {useInView} from "react-intersection-observer";

export const GET_ORGANIZATION_USERS = gql`
query getOrganizationUsers(
	$searchTerm: String = "", 
	$role: userRoleEnum = CUSTOMER,
	$limit: Int
	$offset: Int) {
  organizationUsers(where: {_or: [
		  {firstName: {_iregex: $searchTerm}}, 
		  {lastName: {_iregex: $searchTerm}}, 
		  {phoneNumber: {_iregex: $searchTerm}}
  		], 
	  	_and: {
			role: {_eq: $role}, 
			status: {_neq: SCHEDULED_FOR_DELETION}}
  		}, 
	  orderBy: {lastName: ASC, firstName: ASC},
	  limit:$limit,
	  offset:$offset
  ) {
    id
    firstName
    lastName
    organizationId
    emailAddress
    phoneNumber
    userId
    role
    organization {
      id
      name
    }
    user {
      id
      firstName
      lastName
      lastSeen
      image
      phoneState
      updatedAt
    }
  }
}
`;

//hvis userid er blank så skal det håndteres anerledes
const CREATE_ROOM = gql`
	mutation CreateRoomMutation($id1: uuid = "") {
		createRoom(id1: $id1) {
			id
		}
	}
`;

const UserListItem = ({user, loadMore, idx}) => {

	const router = useRouter();
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: "200px 0px"
	});

	const [active, setActive] = useState(false);
	const [createRoom, { data2, loading2, error2 }] = useMutation(CREATE_ROOM);

	useEffect(() => {
		router.events.on("routeChangeComplete", (url) => {
			const id = url?.split("/").pop();
			setActive(id === user?.id);
		});
	}, []);

	useEffect(() => {
		if (idx !== 0 && idx % 15 === 0 && inView) {
			//console.log("fetch more users", idx);
			loadMore();
		}
	}, [inView]);

	const startMessage = (userId) => {
		createRoom({
			variables: {
				id1: userId
			}
		}).then((result) => {
			const rid = result.data.createRoom.id;
			router.push("/message/" + rid);
		}).catch((error) => {
			console.log("create room, error IS: ", error);
		});
	};

	return (
		<ListItemWrapper
			ref={ref}
			disablePadding
			secondaryAction={
				<>
					{ user.role === "EMPLOYEE" && (
						user.userId ?
							<IconButton
								onClick={
									(e) => {
										e.stopPropagation();
										startMessage(user.userId);
									}
								}
								sx={{}}
							>
								<FontAwesomeIcon icon={faComment} size={"2xs"} color={"#6e6e6e"}/>
							</IconButton>
							:
							user.phoneNumber &&
							<Link
								onClick={(e) => e.stopPropagation()}
								sx={{}}
								href={"sms:" + user.phoneNumber}
								passHref={true}>
								<IconButton>
									<FontAwesomeIcon icon={faMessage} size={"2xs"}
													 color={"#6e6e6e"}/>
								</IconButton>

							</Link>
					)}
					{user.phoneNumber &&
						<Link
							onClick={(e) => e.stopPropagation()}
							sx={{}}
							href={"tel:" + user.phoneNumber}
							passHref={true}>
							<IconButton>
								<FontAwesomeIcon icon={faPhone} size={"2xs"} color={"#6e6e6e"}/>
							</IconButton>

						</Link>}
				</>
			}
		>
			<ListItemButtonWrapper
				height={50}
				key={user.id + idx}
				active={active.toString()}
				onClick={() => {
					if (user.role === "EMPLOYEE") {
						router.push("/employeeprofile/" + user.id);
					}
					else if (user.role === "CUSTOMER") {
						router.push("/contact/" + user.id);
					}
					else if (user.role === "COMPANY") {
						router.push("/contact/" + user.id);
					}
				}}
			>
				<ListItemAvatar>
					<StaticAvatar
						user={user?.role !== "EMPLOYEE" ? user : user?.user}
					/>
				</ListItemAvatar>
				<ListItemText
					sx={{maxWidth: "220px"}}
					primary={`${user.firstName} ${user.lastName ?? ""}`}
					slotProps={{
						primary: {
							fontWeight: "bold",
							overflow: "hidden",
							whiteSpace: "nowrap",
							textOverflow: "ellipsis"
						}
					}}
					secondary={user.phoneNumber}
				/>
			</ListItemButtonWrapper>
		</ListItemWrapper>
	);
};

function Users() {
	const router = useRouter();
	const { t } = useTranslation();
	const {tokenParsed} = useKeycloak();
	const [role, setRole] = useState("EMPLOYEE");
	const [term, setTerm] = useState("");
	const [organizationUsers, setOrganizationUsers] = useState([]);

	const [offset, setOffset] = useState(0);

	const { data, loading, fetchMore } = useQuery(GET_ORGANIZATION_USERS, {
		variables: {
			searchTerm: term,
			role: role,
			limit: 20,
			offset: offset
		}
	});

	useEffect(() => {
		if (data) {
			// console.log("GET_ORGANIZATION_USERS DATA CHANGED: ", data);
			// append new data to existing data
			setOrganizationUsers(data.organizationUsers);
		}
	}, [data]);

	useEffect(() => {
		if (term.length > 0) {
			setOrganizationUsers([]);
		}
	}, [term]);

	return (
		<CardElement
			title={t("Contacts")}
			menuItems={[
				{
					icon: faUser,
					primaryText: "Personal customer",
					secondaryText: "Add new personal customer",
					function: () => { router.push("/newcontact/"); }
				},
				{
					icon: faBuilding,
					primaryText: "Company customer",
					secondaryText: "Add new company customer",
					function: () => { router.push("/newcompany/"); }
				},
				{
					icon: faUserPlus,
					primaryText: "Employee",
					secondaryText: "Add new employee",
					function: () => {}
				}
			]}
			moreActions={[
				{
					// icon: <FontAwesomeIcon icon={faUserPlus} size={"lg"} fixedWidth/>,
					label: t("Customers"),
					cb: () => {
						setTerm("");
						setRole("CUSTOMER");
						setOrganizationUsers([]);
					},
					active: role === "CUSTOMER"
				},
				{
					label: t("Companies"),
					cb: () => {
						setTerm("");
						setRole("COMPANY");
						setOrganizationUsers([]);
					},
					active: role === "COMPANY"
				},
				{
					// icon: <FontAwesomeIcon icon={faBuilding} size={"lg"} fixedWidth/>,
					label: t("Employees"),
					cb: () => {
						setTerm("");
						setRole("EMPLOYEE");
						setOrganizationUsers([]);
					},
					active: role === "EMPLOYEE"
				}
			]}
			search={{
				visible: true,
				searchString: term,
				setSearchString: setTerm,
				placeholder: t("Search")
			}}
		>
			<ListWrapper disablePadding component="nav">
				{organizationUsers?.map((user, i) => {
					if (user && user.id !== tokenParsed?.sub) {
						return (
							<UserListItem
								key={user.id + i}
								user={user}
								idx={i}
								loadMore={() => {
									// console.log("offset: ", organizationUsers.length);
									fetchMore({
										variables: {
											offset: organizationUsers.length
										}
									}).then(
										(result) => {
											//todo Dette er ikke den beste metoden for å håntere dette, Det burde implementeres en cache mekanisme her
											// for å unngå at vi får dobbelte resultater
											// Problemet er bare hvis man scroller veldig fort
											/*
											const mergedResults = [...organizationUsers, ...result.data.organizationUsers];
											// remove duplicates
											const uniqueResults = mergedResults.filter((v, i, a) => a.findIndex((t) => (t.id === v.id)) === i);
											setOrganizationUsers(uniqueResults);
											*/

											setOrganizationUsers((prev) => [...prev, ...result.data.organizationUsers]);
										}
									);
								}}/>
						);
					}
				})}
			</ListWrapper>
		</CardElement>
	);
}

export default Users;
